import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const KINDS = [
  { key: "courier",      label: "Couriers & Transport",
    hint: "Shown as the Transport dropdown on the Order form." },
  { key: "product_unit", label: "Product Units",
    hint: "Shown as the Unit dropdown next to every order/quote line item." },
  { key: "order_source", label: "Order Sources",
    hint: "Shown as the Source dropdown when creating an order." },
];

function ItemDialog({ open, onOpenChange, initial, kind, onSave }) {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [enabled, setEnabled] = useState(true);
  useEffect(() => {
    if (open) {
      setKey(initial?.key || "");
      setLabel(initial?.label || "");
      setEnabled(initial?.enabled ?? true);
    }
  }, [open, initial]);
  const auto = (v) => {
    if (!key) setKey(v.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""));
  };
  const submit = async () => {
    if (!label.trim() || !key.trim()) return toast.error("Label and key required");
    try {
      await onSave({ kind, key: key.trim(), label: label.trim(), enabled });
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="lookup-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit" : "New"} {KINDS.find(k => k.key === kind)?.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">DISPLAY LABEL</Label>
            <Input value={label} onChange={(e) => { setLabel(e.target.value); auto(e.target.value); }}
              placeholder="e.g. Delhivery" data-testid="lookup-label" />
          </div>
          <div>
            <Label className="text-xs">KEY (auto-slugged)</Label>
            <Input className="font-mono" value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]+/g, "_"))}
              disabled={!!initial?.is_system}
              data-testid="lookup-key" />
            {initial?.is_system && <div className="text-[10px] text-muted-foreground mt-1">System entries have locked keys.</div>}
          </div>
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="lookup-enabled" />
            <span>{enabled ? "Visible in dropdowns" : "Hidden from dropdowns"}</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="lookup-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LookupsBuilder() {
  const [kind, setKind] = useState("courier");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/lookups", { params: { kind } });
      setItems(r.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [kind]);

  const save = async (payload) => {
    if (editing?.id) {
      await api.put(`/platform/lookups/${editing.id}`, payload);
      toast.success("Saved");
    } else {
      await api.post("/platform/lookups", payload);
      toast.success("Added");
    }
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.label}"?`)) return;
    try {
      await api.delete(`/platform/lookups/${item.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const toggle = async (item) => {
    await api.put(`/platform/lookups/${item.id}`, { enabled: !item.enabled });
    load();
  };

  const kindMeta = KINDS.find(k => k.key === kind);

  return (
    <Card className="p-6" data-testid="lookups-builder">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <div className="overline text-muted-foreground">Managed Lists</div>
          <h3 className="font-heading font-bold text-xl">Lookups</h3>
          <p className="text-xs text-muted-foreground mt-1">Manage dropdown lists used across the app. Disable an entry to hide it without losing history.</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="lookup-add">
          <Plus size={14} className="mr-1.5" />Add
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="lookup-kind-tabs">
        {KINDS.map((k) => (
          <button key={k.key} onClick={() => setKind(k.key)}
            className={"text-xs px-3 py-1.5 rounded-full border " + (kind === k.key
              ? "bg-primary text-primary-foreground border-primary"
              : "border-border text-muted-foreground hover:bg-secondary")}
            data-testid={`lookup-kind-${k.key}`}>
            {k.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mb-3 italic">{kindMeta?.hint}</p>
      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className={"border rounded-md p-3 flex items-center gap-3 " + (it.enabled ? "" : "opacity-60")}
              data-testid={`lookup-row-${it.key}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm flex items-center gap-2">
                  {it.label}
                  <span className="text-[9px] uppercase text-muted-foreground font-mono">{it.key}</span>
                  {it.is_system && <span className="text-[9px] uppercase text-muted-foreground border px-1 rounded">system</span>}
                  {!it.enabled && <span className="text-[9px] uppercase text-muted-foreground border px-1 rounded">disabled</span>}
                </div>
              </div>
              <label className="flex items-center gap-1.5">
                <Switch checked={it.enabled} onCheckedChange={() => toggle(it)} data-testid={`lookup-toggle-${it.key}`} />
              </label>
              <button onClick={() => { setEditing(it); setDialogOpen(true); }}
                className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit"
                data-testid={`lookup-edit-${it.key}`}>
                <PencilSimple size={13} weight="duotone" />
              </button>
              {!it.is_system && (
                <button onClick={() => remove(it)}
                  className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete"
                  data-testid={`lookup-delete-${it.key}`}>
                  <Trash size={13} weight="duotone" />
                </button>
              )}
            </div>
          ))}
          {!items.length && (
            <div className="py-8 text-center text-muted-foreground text-sm">No entries yet — click Add.</div>
          )}
        </div>
      )}
      <ItemDialog open={dialogOpen} onOpenChange={setDialogOpen}
        initial={editing} kind={kind} onSave={save} />
    </Card>
  );
}
